import React, { FunctionComponent, useRef } from 'react'

import styled, { css, useTheme } from 'styled-components'

import { mobileCSS } from '@measures/responsive'
import { TSourceData } from '@utils/chatbot/types'
import usePopoverPosition, {
  PopoverPositionType,
} from '@hooks/usePopoverPosition'

import useViewportType from '../../hooks/useViewport/useViewportType'
import SvgIcon from '../SvgIcon'

interface SummaryPillProps {
  children: React.ReactNode
  popoverParentRef?: React.RefObject<HTMLElement | null>
  isActive?: boolean
  tooltipItem?: TSourceData
  onTooltipClick?: () => void
}

const Pill = styled.span<
  Pick<SummaryPillProps, 'isActive'> & { hasClickHandler?: boolean }
>`
  ${({
    theme: {
      colors: { staticNeutral400, staticWhite, staticRed900 },
      typography: {
        labels: {
          label3: { bundledCSS: label3Labels },
        },
      },
    },
    isActive,
  }) => css`
    ${label3Labels};
    align-items: center;
    background-color: ${staticNeutral400};
    border-radius: 50%;
    color: ${staticWhite};
    display: inline-flex;
    height: 16px;
    justify-content: center;
    text-align: center;
    width: 16px;

    ${isActive && `background-color: ${staticRed900};`}
  `}
`
const StyledPlusIcon = styled(SvgIcon)`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => {
    return css`
      vertical-align: text-bottom;
      margin-right: ${spacing8};
    `
  }}
`

const TooltipItemWrapper = styled.span<{
  popoverPosition: PopoverPositionType
}>`
  ${({
    popoverPosition,
    theme: {
      colors: { fillSurface, textDefault, strokeWeak },
      spacing: { spacing8, spacing80 },
      typography: {
        bodycopy: {
          xxsmall: { bundledCSS: xxsmallBodyCopy },
        },
      },
    },
  }) => css`
    ${xxsmallBodyCopy};

    display: flex;
    gap: ${spacing8};
    flex-direction: column;
    justify-content: space-between;
    text-decoration: none;
    box-sizing: border-box;
    width: 160px;
    color: ${textDefault};
    background-color: ${fillSurface};
    border: 1px solid ${strokeWeak};
    border-radius: 4px;
    padding: ${spacing8};
    background: ${fillSurface} !important;
    position: absolute;
    bottom: 25px;
    right: -${spacing80};

    &:hover {
      span:last-child {
        background-color: transparent;
      }
    }

    ${popoverPosition === 'top-left' &&
    css`
      right: -${spacing8};
      left: auto;
    `}

    ${popoverPosition === 'top-right' &&
    css`
      left: -${spacing8};
      right: auto;
    `}
    ${popoverPosition === 'top-right' &&
    css`
      left: auto;
    `}
  `}
`

const SummaryPillWrapper = styled.span`
  position: relative;
  display: inline-block;
  background: transparent !important;

  > ${TooltipItemWrapper} {
    display: none;
  }

  &:hover {
    > ${TooltipItemWrapper} {
      display: block;
    }
  }

  ${mobileCSS(css`
    &:hover {
      > ${TooltipItemWrapper} {
        display: none;
      }
    }
  `)}
`

const SummaryPill: FunctionComponent<SummaryPillProps> = ({
  children,
  tooltipItem,
  isActive,
  onTooltipClick,
  popoverParentRef,
}) => {
  const tooltipRef = useRef<HTMLAnchorElement>(null)
  const pillRef = useRef<HTMLAnchorElement>(null)
  const viewportType = useViewportType()
  const isMobile = viewportType !== 'desktop'
  const theme = useTheme()
  const popoverPosition = usePopoverPosition({
    popoverRef: tooltipRef,
    itemRef: pillRef,
    popoverParentRef: popoverParentRef,
  })

  return (
    <SummaryPillWrapper>
      {tooltipItem && !isMobile && (
        <TooltipItemWrapper
          popoverPosition={popoverPosition}
          ref={tooltipRef}
          onClick={() => onTooltipClick && onTooltipClick()}
          key={`source-item-${tooltipItem.url}-${children}`}
        >
          {tooltipItem.isPlus && (
            <StyledPlusIcon
              iconName="blick-plus-logo"
              size={19}
              color={theme.colors.iconDefault}
            />
          )}
          {tooltipItem.title}
        </TooltipItemWrapper>
      )}
      <Pill ref={pillRef} isActive={isActive}>
        {children}
      </Pill>
    </SummaryPillWrapper>
  )
}

export default SummaryPill
